import {useCookie} from './useCookie';
import {useBroadcastChannel, useEventListener} from '@vueuse/core';
import {computed, watch} from 'vue';
import {useDeviceStorage} from './useDeviceStorage/useDeviceStorage';
import {getCookieValue} from '@/api/helpers/cookieHelper';

/**
 * Creates a ref that is synced to the appropriate security storage for the current context.
 *
 * Hybrid app (Capacitor):
 *   Uses secure device storage exclusively.
 *
 * Web app:
 *   - Outside iframes: the cookie is the sole source of truth. A missing cookie means
 *     logged out, even if stale localStorage data remains on a different subdomain.
 *   - Inside iframes: cookies may be blocked by the browser, so localStorage is used
 *     as a fallback.
 *   - Cross-tab reactivity within the same origin is handled via BroadcastChannel.
 *
 * @param key The key used for local storage. The 'security.' prefix is stripped for the cookie key.
 */
export function useSecurityStorage<T extends string | boolean = string>(
    key: string,
    initialValue?: T | null
) {
    const storageValue = useDeviceStorage<T | null>(key, initialValue ?? null);

    // Hybrid app: secure device storage is the sole source of truth.
    if (__IS_HYBRID_APP__) {
        return storageValue;
    }

    const cookieKey = key.replace('security.', '');
    const cookieValue = useCookie<T | null>(cookieKey, initialValue ?? null);

    const {post, data} = useBroadcastChannel<T | null, T | null>({
        name: `security-storage:${key}`,
    });

    useEventListener(document, 'visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            cookieValue.value = getCookieValue(cookieKey) as T | null;
        }
    });

    // When another tab on the same origin changes this value, sync local refs so
    // that Vue's reactivity picks up the change in this tab's computed getter.
    watch(data, newValue => {
        if (newValue !== undefined) {
            cookieValue.value = newValue;
            storageValue.value = newValue;
        }
    });

    return computed({
        get() {
            if (!isInIframe()) {
                // Cookie is authoritative: no cookie → not authenticated,
                // regardless of any leftover localStorage from another subdomain.
                return cookieValue.value;
            }
            // Iframe fallback: cookies may be blocked, use localStorage.
            return cookieValue.value || storageValue.value;
        },
        set(value) {
            cookieValue.value = value;
            storageValue.value = value;
            post(value);
        },
    });
}

function isInIframe(): boolean {
    try {
        return window.self !== window.top;
    } catch {
        // cross-origin iframe — cannot access window.top
        return true;
    }
}
